import { useCallback } from 'react'
import { useDropzone, type DropzoneOptions } from 'react-dropzone'
import { useLibraryStore } from '@/stores/library-store'
import { getAcceptedFileTypes } from '@/lib/schemas'
import { ARCHIVE_ACCEPT_TYPES } from '@/lib/expand-upload-files'
import { prepareUploadsWithFeedback } from '@/lib/upload-feedback'
import { useFolderPicker } from '@/hooks/useFolderPicker'

interface UseDropZoneUploadOptions {
  folderId: string | null
  onUploadComplete?: () => void
  /**
   * Called when files are picked and no folder is selected.
   * When provided, the hook stages the files (no auto-upload) and the consumer
   * opens an explicit destination picker. When omitted, the hook silently
   * drops files in the no-folder case (legacy behaviour for components that
   * only operate inside a folder).
   */
  onFilesStaged?: (files: File[]) => void
}

export function useDropZoneUpload({
  folderId,
  onUploadComplete,
  onFilesStaged,
}: UseDropZoneUploadOptions) {
  const { uploadLibraryDocuments, uploadingFiles } = useLibraryStore()

  // "Disabled" now only means "no folder AND no staging callback" — i.e. there is
  // no productive thing to do with the files. With onFilesStaged, the hook
  // remains active to capture files for the destination picker.
  const isDisabled = folderId === null && !onFilesStaged

  // Check if any files are currently uploading for this folder
  const isUploading = uploadingFiles.some(
    (f) => f.folderId === folderId && (f.status === 'uploading' || f.status === 'pending')
  )

  // Get upload errors for this folder
  const uploadErrors = uploadingFiles
    .filter((f) => f.folderId === folderId && f.status === 'error')
    .map((f) => ({ fileName: f.file.name, error: f.error ?? 'Upload failed' }))

  const handleDrop = useCallback(
    async (dropped: File[]) => {
      if (dropped.length === 0 || isDisabled) return

      // Opens any ZIP, drops system files, applies `validateFile`, and names
      // anything it left out.
      const files = await prepareUploadsWithFeedback(dropped)
      if (files.length === 0) return

      // No folder selected — defer to the staging callback if provided, else no-op
      if (folderId === null) {
        if (onFilesStaged) onFilesStaged(files)
        return
      }

      await uploadLibraryDocuments(folderId, files)
      onUploadComplete?.()
    },
    [folderId, isDisabled, onFilesStaged, uploadLibraryDocuments, onUploadComplete]
  )

  const { openFolderPicker, folderInput } = useFolderPicker({
    onFiles: (files) => {
      void handleDrop(files)
    },
    disabled: isDisabled,
  })

  const dropzoneConfig: DropzoneOptions = {
    // Refused types go through too, so they are named as skipped rather than
    // lost without a word.
    onDrop: (acceptedFiles, fileRejections) => {
      void handleDrop([...acceptedFiles, ...fileRejections.map(({ file }) => file)])
    },
    // ZIPs are opened in the browser, never uploaded.
    accept: { ...getAcceptedFileTypes(), ...ARCHIVE_ACCEPT_TYPES },
    multiple: true,
    disabled: isDisabled,
    noClick: true,
    noKeyboard: true,
  }

  const { getRootProps, getInputProps, isDragActive, open } = useDropzone(dropzoneConfig)

  // Clear errors by removing error files from the store
  const { removeUploadingFile } = useLibraryStore()
  const clearErrors = useCallback(() => {
    uploadingFiles
      .filter((f) => f.folderId === folderId && f.status === 'error')
      .forEach((f) => removeUploadingFile(f.id))
  }, [uploadingFiles, folderId, removeUploadingFile])

  return {
    getRootProps,
    getInputProps,
    open,
    openFolderPicker,
    folderInput,
    isDragActive,
    isUploading,
    isDisabled,
    uploadErrors,
    clearErrors,
  }
}
